/**
 * Catalog → human-readable boilerplate descriptions for the Shop & Kits
 * editor. Picking an item from the catalog auto-suggests one of these
 * sentences when the admin hasn't typed a Description yet, so the
 * public shop reads less "RCON-y" without copywriting work.
 *
 * Strategy:
 *   1. Match the cleaned category (emoji + leading whitespace stripped)
 *      against a hand-curated map of templates per `kind`.
 *   2. Fall back to a generic "{cleaned-category} item" sentence so even
 *      unknown categories produce a passable description.
 *
 * Manual Description input always wins — `addToContents` only calls
 * `describe()` when the field is empty.
 */

// Strip leading emoji + optional space so `"🐾 Pal Saddles"` → `"Pal Saddles"`.
const stripEmoji = (s) =>
  (s || "")
    .replace(/^[\p{Extended_Pictographic}\p{Emoji_Component}\s]+/u, "")
    .trim();

const ITEM_TEMPLATES = {
  "Pal Saddles":           "Saddle for mounting and riding a Pal in the wild.",
  "Pal Harness & Gloves":  "Equipment for your mounted Pal — boosts handling and damage.",
  "Schematics - Headgear": "Blueprint for crafting headgear. Combine with materials at a workbench.",
  "Schematics - Body":     "Blueprint for crafting body armor. Combine with materials at a workbench.",
  "Schematics - Wea...":   "Blueprint for crafting a weapon. Combine with materials at a workbench.",
  "Schematics - Weapons":  "Blueprint for crafting a weapon. Combine with materials at a workbench.",
  "Schematics - Hea...":   "Blueprint for crafting headgear. Combine with materials at a workbench.",
  "Schematics - Other":    "Crafting blueprint. Combine with materials at a workbench.",
  "Bows & Crossbows":      "Ranged weapon — quiet and effective at short-to-medium range.",
  "Ammo":                  "Ammunition for ranged weapons.",
  "Sphere Modules":        "Modifier that attaches to a Pal Sphere to improve capture odds.",
  "Building Materials":    "Construction material for crafting buildings and structures.",
  "Medical Supplies":      "Consumable that restores HP or cures a status effect.",
  "Food - Cooked":         "Cooked dish that restores HP and SAN.",
  "Food - Raw":            "Raw ingredient. Cook it for higher restoration.",
  "Skill Fruits":          "Feed to a Pal to teach it a new active skill.",
  "Fishing":               "Fishing equipment or bait.",
  "Grenades & Traps":      "Explosive ordnance. Handle with care.",
  "Accessories":           "Wearable accessory that grants a passive effect.",
  "Gems":                  "Crafting reagent harvested from gem nodes.",
  "Stones & Crystals":     "Refined crystal used in advanced crafting.",
  "Ancient Parts":         "Rare ancient artifact required for late-game tech.",
  "Manuals":               "Instructional manual — read to unlock or improve a skill.",
  "Pal Souls":             "Soul shard used to enhance a Pal's stats.",
  "Raid Slabs":            "Summoning slab — initiates a raid event.",
  "Bounty Tokens":         "Bounty token redeemable for rewards at the Bounty NPC.",
  "Weapons - Heavy":       "Heavy weapon. High damage, slow rate of fire.",
  "Weapons - Energy":      "Energy weapon — uses recharge batteries instead of ammo.",
  "Weapons - Rifles":      "Rifle. Accurate at long range.",
  "Weapons - Melee":       "Melee weapon for close-quarters combat.",
  "Weapons - Shotguns":    "Shotgun. Devastating at close range.",
  "Shields":               "Shield that absorbs incoming damage.",
  "Seeds":                 "Plant in a base farm to grow a renewable harvest.",
  "Other":                 "Miscellaneous item.",
};

const PAL_DEFAULT = "Wild Pal — give to spawn a level-1 instance with random IVs.";
const PAL_BOSS    = "Boss variant Pal — larger, stronger, with rarer drops.";

const EGG_DEFAULT = "Pal egg. Incubate at an Egg Incubator to hatch a Pal of the matching element.";

const TECH_DEFAULT = "Tech unlock that grants access to a new crafting recipe.";

/**
 * Return a one-sentence description for a catalog pick, or `null` if
 * nothing meaningful can be inferred. The kit editor only uses this
 * when the Description field is empty.
 *
 * @param {{ name: string, category?: string }} item - Catalog item.
 * @param {"item" | "pal" | "egg" | "tech"} kind   - Picker tab.
 */
export function describe(item, kind) {
  if (!item) return null;
  const category = stripEmoji(item.category);

  if (kind === "pal") {
    if (/^Boss\b/i.test(category)) return PAL_BOSS;
    return PAL_DEFAULT;
  }
  if (kind === "egg")  return EGG_DEFAULT;
  if (kind === "tech") return TECH_DEFAULT;

  // items
  if (ITEM_TEMPLATES[category]) return ITEM_TEMPLATES[category];
  // Generic fallback — at least surface the category so the shop card
  // doesn't look bare.
  return category ? `${category} item.` : null;
}
